/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { ReduxUtils } from '@spm/core';

import {
  FETCH_SD_EXTERNALUSER_RENEWAL_STATUS,
  FETCH_SD_RENEWAL_APPLICATION_FORM,
  FETCH_SDCCA_CCA_RENEWAL_APPLICATION_FORM,
} from './actionTypes';

import {
  fetchSdExternaluserRenewalStatusUtil,
  fetchSdRenewalApplicationFormUtil,
  fetchSdccaCcaRenewalApplicationFormUtil,
} from './utils';

/**
 * Contains the action creator functions that update the UserRenewals slice of the store.
 *
 * @namespace UserRenewalsActions
 */
export default class actions {
  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/sd_externaluser_renewal_status
   *
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof UserRenewalsActions
   */
  static fetchSdExternaluserRenewalStatus = (dispatch, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      fetchSdExternaluserRenewalStatusUtil,
      FETCH_SD_EXTERNALUSER_RENEWAL_STATUS,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchSdExternaluserRenewalStatus.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof UserRenewalsActions
   */
  static resetFetchSdExternaluserRenewalStatus = (dispatch, data) => {
    const json = {
      type: FETCH_SD_EXTERNALUSER_RENEWAL_STATUS.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchSdExternaluserRenewalStatus.
   *
   * @param {function} dispatch
   * @function
   * @memberof UserRenewalsActions
   */
  static resetFetchSdExternaluserRenewalStatusError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_SD_EXTERNALUSER_RENEWAL_STATUS.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/sd_renewal_application_form
   *
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof UserRenewalsActions
   */
  static fetchSdRenewalApplicationForm = (dispatch, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      fetchSdRenewalApplicationFormUtil,
      FETCH_SD_RENEWAL_APPLICATION_FORM,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchSdRenewalApplicationForm.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof UserRenewalsActions
   */
  static resetFetchSdRenewalApplicationForm = (dispatch, data) => {
    const json = {
      type: FETCH_SD_RENEWAL_APPLICATION_FORM.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchSdRenewalApplicationForm.
   *
   * @param {function} dispatch
   * @function
   * @memberof UserRenewalsActions
   */
  static resetFetchSdRenewalApplicationFormError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_SD_RENEWAL_APPLICATION_FORM.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/sdcca_cca_renewal_application_form
   *
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof UserRenewalsActions
   */
  static fetchSdccaCcaRenewalApplicationForm = (dispatch, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      fetchSdccaCcaRenewalApplicationFormUtil,
      FETCH_SDCCA_CCA_RENEWAL_APPLICATION_FORM,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchSdccaCcaRenewalApplicationForm.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof UserRenewalsActions
   */
  static resetFetchSdccaCcaRenewalApplicationForm = (dispatch, data) => {
    const json = {
      type: FETCH_SDCCA_CCA_RENEWAL_APPLICATION_FORM.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchSdccaCcaRenewalApplicationForm.
   *
   * @param {function} dispatch
   * @function
   * @memberof UserRenewalsActions
   */
  static resetFetchSdccaCcaRenewalApplicationFormError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_SDCCA_CCA_RENEWAL_APPLICATION_FORM.failureAction);
  };
}
